import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, Star, Sparkles, Leaf, Loader2 } from "lucide-react";

interface AmazonPick {
  asin: string;
  title: string;
  brand?: string | null;
  image?: string | null;
  price_display?: string | null;
  rating?: number | null;
  review_count?: number | null;
  detail_url: string;
}

const MONTH_TASKS: Record<string, string> = {
  january: "winter pruning, frost protection, planting bare-root trees",
  february: "seed starting, soil preparation, indoor sowing",
  march: "lawn care start, pruning roses, sowing hardy annuals",
  april: "spring planting, lawn feeding, weed control",
  may: "lawn mowing, hanging baskets, vegetable planting, hedge trimming",
  june: "watering, deadheading, summer pruning, BBQ season",
  july: "watering, weed control, harvesting, summer lawn care",
  august: "watering, pest control, autumn prep, deadheading",
  september: "lawn renovation, leaf clearance, autumn planting",
  october: "leaf clearing, lawn aeration, hedge trimming, bulb planting",
  november: "leaf blowing, garden tidying, frost protection, log splitting",
  december: "tool storage, snow clearing, winter pruning, indoor plants",
};

const MONTH_NAMES = ["january","february","march","april","may","june","july","august","september","october","november","december"];

interface Props {
  /** month slug like "may"; defaults to current month */
  month?: string;
  itemCount?: number;
  category?: string;
}

export function SeasonalAmazonPicks({ month, itemCount = 6, category }: Props) {
  const m = (month || MONTH_NAMES[new Date().getMonth()]).toLowerCase();
  const monthLabel = m.charAt(0).toUpperCase() + m.slice(1);
  const tasks = MONTH_TASKS[m] || "essential gardening";
  const keywords = category
    ? `${category} for ${monthLabel} gardening UK`
    : `garden tools for ${monthLabel} ${tasks.split(",")[0].trim()} UK`;

  // Lazy-mount: only fetch when scrolled near viewport, so it doesn't slow first paint
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    if (enabled || !sentinelRef.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setEnabled(true);
          obs.disconnect();
        }
      },
      { rootMargin: "400px" }
    );
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [enabled]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["seasonal-amazon-picks", m, category, itemCount],
    enabled,
    staleTime: 6 * 60 * 60 * 1000, // 6h client cache (matches edge cache)
    gcTime: 24 * 60 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("generate-comparison", {
        body: {
          // No prompt — skipAi true uses PA-API's native ranking, no AI round-trip
          keywords,
          searchIndex: "All",
          itemCount,
          sortBy: "AvgCustomerReviews",
          skipAi: true,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      return (data?.items || []) as AmazonPick[];
    },
  });

  if (!enabled) {
    return <div ref={sentinelRef} className="my-10 min-h-[120px]" aria-hidden="true" />;
  }

  if (isLoading) {
    return (
      <section className="my-10">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="h-5 w-5 text-primary" />
          <h2 className="text-2xl font-bold">Loading {monthLabel}'s top picks…</h2>
        </div>
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      </section>
    );
  }

  if (isError || !data?.length) return null;

  return (
    <section className="my-10">
      <div className="flex items-start justify-between gap-4 mb-2">
        <div>
          <Badge variant="secondary" className="mb-2 gap-1">
            <Leaf className="h-3 w-3" /> Seasonal · {monthLabel}
          </Badge>
          <h2 className="text-2xl md:text-3xl font-bold">Best products for {monthLabel} gardening</h2>
          <p className="text-muted-foreground mt-1">
            Live UK picks from Amazon, refreshed automatically. Highest customer ratings for {tasks}.
          </p>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mb-4">
        As an Amazon Associate we earn from qualifying purchases. Prices accurate at time of viewing.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {data.map((it) => (
          <Card key={it.asin} className="flex flex-col bg-white overflow-hidden">
            <div className="relative bg-muted/30 aspect-square flex items-center justify-center p-4">
              {it.image ? (
                <img src={it.image} alt={it.title} loading="lazy" className="max-h-full max-w-full object-contain" />
              ) : <div className="text-sm text-muted-foreground">No image</div>}
            </div>
            <div className="p-4 flex-1 flex flex-col">
              {it.brand && <div className="text-xs text-muted-foreground uppercase mb-1">{it.brand}</div>}
              <h3 className="text-sm font-semibold line-clamp-2 mb-2">{it.title}</h3>
              {it.rating != null && (
                <div className="flex items-center gap-1 text-sm mb-2">
                  <Star className="h-4 w-4 fill-yellow-500 text-yellow-500" />
                  <span>{Number(it.rating).toFixed(1)}</span>
                  {it.review_count ? <span className="text-muted-foreground">({it.review_count.toLocaleString()})</span> : null}
                </div>
              )}
              {it.price_display && <div className="text-xl font-bold mb-3">{it.price_display}</div>}
              <Button asChild className="mt-auto w-full" size="sm">
                <a href={it.detail_url} target="_blank" rel="noopener noreferrer sponsored">
                  Check on Amazon <ExternalLink className="h-4 w-4 ml-1" />
                </a>
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </section>
  );
}
